import React from 'react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

// Single source of truth for each infrastructure service's chart/table/note
// JSX. The compact stat cells come from serviceStats.js; this module owns the
// remaining visual detail (recharts charts, the RDS table, the Lambda mini-list
// and note) so the inline service card and any other consumer draw from ONE
// chart definition and cannot drift.
//
// Each entry is (d, accountData) => JSX | null, where `d` is the service's
// slice of accountData (accountData[key] || {}). Every guard condition and data
// key is preserved verbatim from the original per-service detail renderers, so
// nothing a user could see disappears. Services without a chart are simply
// absent from the map (getServiceCharts returns null); the card then shows just
// the numeric stat grid. No-fabrication: `|| 0` is used only for chart series
// math and guard conditions, never to fabricate a displayed numeric metric
// (those still come from serviceStats.js).

// Inside-slice percentage label for the donut charts. Positioned at the radial
// midpoint of each slice so labels never clip against the chart edge; hidden for
// very thin slices to avoid clutter.
export const renderInsidePieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  if (percent == null || percent < 0.06) return null;
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="#ffffff" fontSize={11} fontWeight={600}
          textAnchor="middle" dominantBaseline="central">
      {`${Math.round(percent * 100)}%`}
    </text>
  );
};

const SERVICE_CHARTS = {
  lambda: (d) => (
    <>
      {(d.invocations || 0) > 0 && (
        <div className="chart-card">
          <div className="chart-title">Executions (24h)</div>
          <ResponsiveContainer width="100%" height={160}>
            <LineChart data={d.executionTrend || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="time" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="success" stroke="#00A651" strokeWidth={2} />
              <Line type="monotone" dataKey="errors" stroke="#E53935" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      {Object.keys(d.runtimeDistribution || {}).length > 0 && (
        <div className="chart-card">
          <div className="chart-title">Runtime Distribution</div>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={Object.entries(d.runtimeDistribution).map(([runtime, count]) => ({ runtime, count }))}>
              <CartesianGrid strokeDasharray="3 3" />
              {/* Runtime names are hidden on the axis (they skip/overlap and
                  drift out of alignment with the bars); the exact runtime is
                  shown in the tooltip on hover. */}
              <XAxis dataKey="runtime" tick={false} axisLine={false} height={8} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" fill="#0051A3" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      {(d.functionsThrottled || 0) > 0 && (
        <div className="metrics-grid-4" style={{ marginTop: '12px' }}>
          <div className="metric-widget">
            <div className="widget-label">Functions Throttled</div>
            <div className="widget-value warning">{d.functionsThrottled || 0}</div>
          </div>
        </div>
      )}
      {(d.errorFunctions || []).length > 0 && (
        <div className="chart-card">
          <div className="chart-title">Top Error-Prone Functions</div>
          <div className="mini-list">
            {(d.errorFunctions || []).map((fn, idx) => (
              <div key={idx} className="mini-list-row">
                <span className="mini-list-name" title={fn.name}>{fn.name}</span>
                <span className="mini-list-value error">{fn.errorRate}%</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  ),

  ec2: (d) => (
    <>
      {(d.cpuTrend || []).length > 0 && (
        <div className="chart-card">
          <div className="chart-title">CPU Utilization (24h)</div>
          <ResponsiveContainer width="100%" height={160}>
            <LineChart data={d.cpuTrend || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="time" />
              <YAxis unit="%" domain={[0, 100]} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="avgCpu" name="Avg CPU" stroke="#0051A3" strokeWidth={2} />
              <Line type="monotone" dataKey="maxCpu" name="Peak CPU" stroke="#E53935" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      {((d.running || 0) > 0 || (d.stopped || 0) > 0) && (
        <div className="chart-card">
          <div className="chart-title">Instance Types</div>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={d.instanceTypes || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="type" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#0051A3" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </>
  ),

  s3: (d) => (
    <>
      {(d.bucketBreakdown || []).length > 0 && (
        <div className="chart-card">
          <div className="chart-title">Top Buckets by Size (GB)</div>
          <ResponsiveContainer width="100%" height={Math.max(160, (d.bucketBreakdown.length * 40) + 60)}>
            <BarChart data={d.bucketBreakdown} layout="vertical" margin={{ left: 20, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" unit=" GB" />
              <YAxis type="category" dataKey="name" width={220} interval={0} tick={{ fontSize: 11 }} />
              <Tooltip labelFormatter={(label, payload) => (payload && payload[0] && payload[0].payload.fullName) || label} />
              <Bar dataKey="sizeGB" name="Size (GB)" fill="#0051A3" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </>
  ),

  rds: (d) => (
    <>
      {(d.instanceDetails || []).length > 0 && (
        <div className="chart-card" style={{ marginTop: '12px' }}>
          <div className="chart-title">DB Instances</div>
          <div className="rds-table-wrap">
            <table className="rds-table">
              <thead>
                <tr>
                  <th>Instance</th>
                  <th>Engine</th>
                  <th>Class</th>
                  <th>Status</th>
                  <th className="num">CPU</th>
                  <th className="num">Conns</th>
                  <th className="num">Storage</th>
                  <th className="num">Free</th>
                  <th className="num">Read</th>
                  <th className="num">Write</th>
                  <th className="center">Multi-AZ</th>
                </tr>
              </thead>
              <tbody>
                {(d.instanceDetails || []).map((inst, idx) => (
                  <tr key={idx}>
                    <td className="rds-name" title={inst.id}>{inst.id}</td>
                    <td>{inst.engine || '—'}</td>
                    <td>{inst.class || '—'}</td>
                    <td>
                      <span className={`rds-status ${inst.status === 'available' ? 'ok' : 'warn'}`}>
                        {inst.status || '—'}
                      </span>
                    </td>
                    <td className={`num ${(inst.cpu || 0) > 80 ? 'error' : (inst.cpu || 0) > 60 ? 'warning' : ''}`}>
                      {inst.cpu != null ? `${inst.cpu}%` : '—'}
                    </td>
                    <td className="num">{inst.connections != null ? inst.connections : '—'}</td>
                    <td className="num">{inst.storage != null ? `${inst.storage} GB` : '—'}</td>
                    <td className="num">{inst.freeStorageGB != null ? `${inst.freeStorageGB} GB` : '—'}</td>
                    <td className="num">{inst.readLatencyMs != null ? `${inst.readLatencyMs} ms` : '—'}</td>
                    <td className="num">{inst.writeLatencyMs != null ? `${inst.writeLatencyMs} ms` : '—'}</td>
                    <td className="center">{inst.multiAZ ? '✓' : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  ),

  apigateway: (d) => (
    <>
      {(d.requests || 0) > 0 && (
        <div className="chart-card">
          <div className="chart-title">Requests (24h)</div>
          <ResponsiveContainer width="100%" height={160}>
            <LineChart data={d.requestTrend || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="time" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="requests" stroke="#0051A3" strokeWidth={2} name="Requests" />
              {/* Only plot errors when the collector actually reported them.
                  An all-null series would otherwise add a legend entry for data
                  we do not have. */}
              {(d.requestTrend || []).some((p) => p && p.errors != null) && (
                <Line type="monotone" dataKey="errors" stroke="#E53935" strokeWidth={2} name="Errors" />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </>
  ),

  ecs: (d) => (
    <>
      {(d.serviceBreakdown || []).length > 0 && (
        <div className="chart-card">
          <div className="chart-title">
            {(() => {
              const shown = d.serviceBreakdown.length;
              const total = typeof d.services === 'number' ? d.services : null;
              // Only call it a "top N of M" view when there are genuinely more
              // services than the chart shows; otherwise it IS the full set.
              const isSubset = (d.serviceBreakdownIsTopN === true) || (total != null && total > shown);
              return isSubset
                ? `CPU vs Memory by Service (24h avg) — top ${shown}${total != null ? ` of ${total}` : ''}`
                : 'CPU vs Memory by Service (24h avg)';
            })()}
          </div>
          <ResponsiveContainer width="100%" height={Math.max(160, (d.serviceBreakdown.length * 44) + 60)}>
            <BarChart data={d.serviceBreakdown} layout="vertical" margin={{ left: 20, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" unit="%" domain={[0, 100]} />
              <YAxis type="category" dataKey="name" width={220} interval={0} tick={{ fontSize: 11 }} />
              <Tooltip labelFormatter={(label, payload) => (payload && payload[0] && payload[0].payload.fullName) || label} />
              <Legend />
              <Bar dataKey="cpu" name="CPU %" fill="#0051A3" />
              <Bar dataKey="memory" name="Memory %" fill="#00A651" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </>
  ),

  stepfunctions: (d) => {
    const outcomeData = [
      { name: 'Succeeded', value: d.succeeded || 0, color: '#00A651' },
      { name: 'Failed', value: d.failed || 0, color: '#E53935' },
      { name: 'Running', value: d.running || 0, color: '#0051A3' },
      { name: 'Timed Out', value: d.timedOut || 0, color: '#FB8C00' },
      { name: 'Aborted', value: d.aborted || 0, color: '#8E24AA' },
    ].filter(x => x.value > 0);
    return (
      <>
        {(d.executions || 0) > 0 && (
          <div className="chart-card">
            <div className="chart-title">Execution Outcomes (24h)</div>
            <ResponsiveContainer width="100%" height={190}>
              <PieChart>
                <Pie
                  data={outcomeData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="45%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={2}
                  labelLine={false}
                  label={renderInsidePieLabel}
                >
                  {outcomeData.map((entry, idx) => (
                    <Cell key={idx} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
        {(d.stateMachineBreakdown || []).length > 0 && (
          <div className="chart-card">
            <div className="chart-title">
              {(() => {
                const shown = d.stateMachineBreakdown.length;
                const total = typeof d.stateMachines === 'number' ? d.stateMachines : null;
                // Only label it "top N of M" when more state machines exist than
                // the chart shows; otherwise it IS the full set.
                const isSubset = (d.stateMachineBreakdownIsTopN === true) || (total != null && total > shown);
                return isSubset
                  ? `Executions by State Machine (24h) — top ${shown}${total != null ? ` of ${total}` : ''}`
                  : 'Executions by State Machine (24h)';
              })()}
            </div>
            <ResponsiveContainer width="100%" height={Math.max(160, (d.stateMachineBreakdown.length * 44) + 60)}>
              <BarChart data={d.stateMachineBreakdown} layout="vertical" margin={{ left: 20, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" allowDecimals={false} />
                <YAxis type="category" dataKey="name" width={220} interval={0} tick={{ fontSize: 11 }} />
                <Tooltip labelFormatter={(label, payload) => (payload && payload[0] && payload[0].payload.fullName) || label} />
                <Legend />
                <Bar dataKey="succeeded" name="Succeeded" stackId="a" fill="#00A651" />
                <Bar dataKey="failed" name="Failed" stackId="a" fill="#E53935" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </>
    );
  },

  guardduty: (d) => (
    <>
      {((d.highSeverity || 0) > 0 || (d.mediumSeverity || 0) > 0) && (
        <div className="chart-card">
          <div className="chart-title">Findings by Severity (30d)</div>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={[
              { severity: 'High', count: d.highSeverity || 0, color: '#E53935' },
              { severity: 'Medium', count: d.mediumSeverity || 0, color: '#FB8C00' },
            ]}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="severity" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" name="Findings">
                {[{ color: '#E53935' }, { color: '#FB8C00' }].map((entry, idx) => (
                  <Cell key={idx} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </>
  ),

  elasticache: (d) => {
    const engineData = [
      { name: 'Redis', value: d.redis || 0, color: '#C6282C' },
      { name: 'Memcached', value: d.memcached || 0, color: '#0051A3' },
    ].filter(x => x.value > 0);
    return (
      <>
        {engineData.length > 0 && (
          <div className="chart-card">
            <div className="chart-title">Engine Mix</div>
            <ResponsiveContainer width="100%" height={190}>
              <PieChart>
                <Pie
                  data={engineData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="45%"
                  innerRadius={45}
                  outerRadius={75}
                  paddingAngle={2}
                  labelLine={false}
                  label={renderInsidePieLabel}
                >
                  {engineData.map((entry, idx) => (
                    <Cell key={idx} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </>
    );
  },

  elb: (d) => (
    <>
      {((d.alb || 0) > 0 || (d.nlb || 0) > 0 || (d.classic || 0) > 0) && (
        <div className="chart-card">
          <div className="chart-title">Load Balancers by Type</div>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={[
              { type: 'Application', count: d.alb || 0 },
              { type: 'Network', count: d.nlb || 0 },
              { type: 'Classic', count: d.classic || 0 },
            ]}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="type" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" name="Load Balancers" fill="#0051A3" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </>
  ),

  sqs: (d) => (
    <>
      {((d.messagesVisible || 0) > 0 || (d.messagesInFlight || 0) > 0 || (d.dlqCount || 0) > 0) && (
        <div className="chart-card">
          <div className="chart-title">Message States</div>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={[
              { state: 'Visible', count: d.messagesVisible || 0, color: '#0051A3' },
              { state: 'In Flight', count: d.messagesInFlight || 0, color: '#FB8C00' },
              { state: 'DLQs', count: d.dlqCount || 0, color: '#E53935' },
            ]}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="state" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" name="Count">
                {[{ color: '#0051A3' }, { color: '#FB8C00' }, { color: '#E53935' }].map((entry, idx) => (
                  <Cell key={idx} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </>
  ),

  ecr: (d) => (
    <>
      {((d.criticalVulnerabilities || 0) > 0 || (d.highVulnerabilities || 0) > 0) && (
        <div className="chart-card">
          <div className="chart-title">Vulnerabilities by Severity</div>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={[
              { severity: 'Critical', count: d.criticalVulnerabilities || 0, color: '#E53935' },
              { severity: 'High', count: d.highVulnerabilities || 0, color: '#FB8C00' },
            ]}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="severity" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" name="Findings">
                {[{ color: '#E53935' }, { color: '#FB8C00' }].map((entry, idx) => (
                  <Cell key={idx} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </>
  ),

  eventbridge: (d) => {
    const ruleTypeData = [
      { name: 'Scheduled', value: d.scheduledRules || 0, color: '#0051A3' },
      { name: 'Event Pattern', value: d.eventPatternRules || 0, color: '#00A651' },
    ].filter(x => x.value > 0);
    return (
      <>
        {ruleTypeData.length > 0 && (
          <div className="chart-card">
            <div className="chart-title">Rule Types</div>
            <ResponsiveContainer width="100%" height={190}>
              <PieChart>
                <Pie
                  data={ruleTypeData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="45%"
                  innerRadius={45}
                  outerRadius={75}
                  paddingAngle={2}
                  labelLine={false}
                  label={renderInsidePieLabel}
                >
                  {ruleTypeData.map((entry, idx) => (
                    <Cell key={idx} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </>
    );
  },

  glue: (d) => (
    <>
      {((d.succeededJobs24h || 0) > 0 || (d.failedJobs24h || 0) > 0 || (d.runningJobs || 0) > 0) && (
        <div className="chart-card">
          <div className="chart-title">Job Outcomes (24h)</div>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={[
              { state: 'Succeeded', count: d.succeededJobs24h || 0, color: '#00A651' },
              { state: 'Failed', count: d.failedJobs24h || 0, color: '#E53935' },
              { state: 'Running', count: d.runningJobs || 0, color: '#0051A3' },
            ]}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="state" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" name="Jobs">
                {[{ color: '#00A651' }, { color: '#E53935' }, { color: '#0051A3' }].map((e, idx) => (
                  <Cell key={idx} fill={e.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </>
  ),

  mwaa: (d) => (
    <>
      {((d.taskSuccess24h || 0) > 0 || (d.taskFailed24h || 0) > 0 || (d.taskRunning || 0) > 0 || (d.taskQueued || 0) > 0) && (
        <div className="chart-card">
          <div className="chart-title">Task Outcomes (24h)</div>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={[
              { state: 'Success', count: d.taskSuccess24h || 0 },
              { state: 'Failed', count: d.taskFailed24h || 0 },
              { state: 'Running', count: d.taskRunning || 0 },
              { state: 'Queued', count: d.taskQueued || 0 },
            ]}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="state" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" name="Tasks">
                {['#00A651', '#E53935', '#0051A3', '#FB8C00'].map((c, idx) => (
                  <Cell key={idx} fill={c} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </>
  ),

  backup: (d) => (
    <>
      {((d.completedJobs24h || 0) > 0 || (d.failedJobs24h || 0) > 0 || (d.runningJobs || 0) > 0) && (
        <div className="chart-card">
          <div className="chart-title">Backup Jobs (24h)</div>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={[
              { state: 'Completed', count: d.completedJobs24h || 0 },
              { state: 'Failed', count: d.failedJobs24h || 0 },
              { state: 'Running', count: d.runningJobs || 0 },
            ]}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="state" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" name="Jobs">
                {['#00A651', '#E53935', '#0051A3'].map((c, idx) => (
                  <Cell key={idx} fill={c} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </>
  ),

  config: (d) => {
    const complianceData = [
      { name: 'Compliant', value: d.compliantRules || 0, color: '#00A651' },
      { name: 'Non-Compliant', value: d.nonCompliantRules || 0, color: '#E53935' },
    ].filter(x => x.value > 0);
    return (
      <>
        {complianceData.length > 0 && (
          <div className="chart-card">
            <div className="chart-title">Rule Compliance</div>
            <ResponsiveContainer width="100%" height={190}>
              <PieChart>
                <Pie
                  data={complianceData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="45%"
                  innerRadius={45}
                  outerRadius={75}
                  paddingAngle={2}
                  labelLine={false}
                  label={renderInsidePieLabel}
                >
                  {complianceData.map((entry, idx) => (
                    <Cell key={idx} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </>
    );
  },

  codepipeline: (d) => (
    <>
      {((d.succeeded || 0) > 0 || (d.failed || 0) > 0 || (d.inProgress || 0) > 0) && (
        <div className="chart-card">
          <div className="chart-title">Pipeline Status</div>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={[
              { state: 'Succeeded', count: d.succeeded || 0 },
              { state: 'Failed', count: d.failed || 0 },
              { state: 'In Progress', count: d.inProgress || 0 },
            ]}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="state" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" name="Pipelines">
                {['#00A651', '#E53935', '#FB8C00'].map((c, idx) => (
                  <Cell key={idx} fill={c} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </>
  ),
};

// Resolve a service's chart body. Returns the rendered node, or null when the
// service has no dedicated chart (stats-only services). The card renders the
// node only when it is truthy.
export function getServiceCharts(serviceKey, d, accountData) {
  const render = SERVICE_CHARTS[serviceKey];
  if (!render) return null;
  return render(d || {}, accountData);
}
